import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowSquareOut, At, CalendarBlank, CalendarCheck, ChatCircleText, ChartPieSlice, Check, CrownSimple, DeviceMobile, Envelope, EnvelopeSimple, FolderSimple, Hash, Heart,
  MagnifyingGlass, Mailbox, MapPin, MapTrifold, Megaphone, Note, Percent, Phone, Plus, SealCheck, Signature, Signpost, Sparkle, TextAlignLeft, Trash,
  TreeStructure, UploadSimple, User, Users, UsersThree, X, type Icon,
} from '@phosphor-icons/react';
import {
  Auswahl, BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, PanelZeile, SchalterZeile, SegmentedControl, TextFeld,
  ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { useIslandActions } from '../../../../../components/island/IslandProvider';
import { eur } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { normalisiereSuche } from '../../../../../lib/immobilien/suche';
import { logAktivitaet, loescheNotiz, speichereNotiz } from '../../../../../lib/kontakte/api';
import { ladeMieterDokumentHoch, oeffneMieterDokument } from '../../../../../lib/mieter/mieterDokumente';
import { EIG_STATUS, PORTAL_ANZEIGE, statusText } from '../../../../../lib/eigentuemer/liste';
import { meldeEigentuemerGeaendert } from '../../../../../lib/eigentuemer/useEigentuemerDetail';
import { freigabeKennzahlen } from '../../../../../lib/eigentuemer/verwaltung';
import {
  adressText, artLabel, beteiligtenFehler, beteiligtenPayload, eigenerAnteil, hatAbweichendeAdresse, MIT_ARTEN, POST_ARTEN, postPflicht, textformWarnung, VERSANDWEGE,
  versandweg, VERTRETER_ARTEN, vollmachtGueltig, zustellvertreterFehlt, type Beteiligter, type BeteiligtenEntwurf, type MitArt, type Versandweg, type VertreterArt,
  type ZustellAdresse,
} from '../../../../../lib/eigentuemer/beteiligte';
import {
  legeBeteiligtenAn, loescheBeteiligten, setzeBeteiligtenDokument, setzeZustellvertreter, speichereZustellung, type ZustellFelder,
} from '../../../../../lib/eigentuemer/aktionen';
import { AktivitaetBereich } from '../../../objekte/gemeinsam/WeitereBereiche';
import { TerminFormularKarte } from '../../../objekte/gemeinsam/TerminFormularKarte';
import { DatenZeile } from '../../../objekte/einheit/bereiche/teile';
import { useEigSeite } from '../EigentuemerSeitenKontext';

const GLAS: React.CSSProperties = { background: 'var(--glass)' };
const fehlerText = (e: unknown) => (e instanceof Error ? e.message : 'Unbekannter Fehler');
const ZEIT = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });
const tausend = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });

// ── Miteigentümer & Vertreter (Screens 51, 52, 67) ─────────────────────────────
const LEER_MIT = { name: '', anteil: '', email: '' };
const LEER_VTR = { name: '', gueltigBis: '', umfang: '' };
const MIT_ICON: Record<MitArt, Icon> = { miteigentum: Users, erbengemeinschaft: TreeStructure, ehepartner: Heart };

export function BeteiligtePanel() {
  const s = useEigSeite();
  const openKontakt = useOpenKontakt();
  const eig = s.daten.eigentuemer;
  const [toast, zeige] = useToastPill();
  const [mitArt, setMitArt] = useState<MitArt>('miteigentum');
  const [mit, setMit] = useState(LEER_MIT);
  const [vtrArt, setVtrArt] = useState<VertreterArt>('vorsorgevollmacht');
  const [vtr, setVtr] = useState(LEER_VTR);
  const [vollmacht, setVollmacht] = useState<File | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [loeschen, setLoeschen] = useState<Beteiligter | null>(null);
  const datei = useRef<HTMLInputElement>(null);
  const alle = s.daten.beteiligte;
  const mitglieder = alle.filter(b => b.typ === 'miteigentuemer');
  const vertreter = alle.filter(b => b.typ === 'vertreter');
  const sprecher = alle.find(b => b.zustellvertreter) ?? null;
  const anteil = eigenerAnteil(alle);
  const fehlt = zustellvertreterFehlt(alle);
  const einheitName = new Map(s.daten.einheiten.map(e => [e.id, (e.bezeichnung ?? '').trim() || 'Einheit']));
  // weitere laufende Eigentümer derselben Einheiten (einheit_eigentuemer mit Historie) – eigene Eigentümer-Datensätze
  const ausEinheiten = useMemo(() => {
    const je = new Map<string, { id: string; name: string; einheiten: string[] }>();
    for (const m of s.daten.miteigentuemer) {
      const p = je.get(m.id) ?? { id: m.id, name: m.name, einheiten: [] };
      p.einheiten.push(einheitName.get(m.einheitId) ?? 'Einheit');
      je.set(m.id, p);
    }
    return [...je.values()];
  }, [s.daten.miteigentuemer, einheitName]);

  const mitEntwurf: BeteiligtenEntwurf = { typ: 'miteigentuemer', art: mitArt, name: mit.name, email: mit.email, anteil: mit.anteil, umfang: '', gueltigBis: '' };
  const vtrEntwurf: BeteiligtenEntwurf = { typ: 'vertreter', art: vtrArt, name: vtr.name, email: '', anteil: '', umfang: vtr.umfang, gueltigBis: vtr.gueltigBis };
  const mitFehler = mit.name.trim() || mit.anteil.trim() || mit.email.trim() ? beteiligtenFehler(mitEntwurf, alle, s.heute) : null;
  const vtrFehler = vtr.name.trim() || vtr.gueltigBis ? beteiligtenFehler(vtrEntwurf, alle, s.heute) : null;

  const ausfuehren = async (fn: () => Promise<void>, text: string) => {
    setLaeuft(true);
    try { await fn(); zeige(text); meldeEigentuemerGeaendert(eig.id); } catch (e) { zeige(fehlerText(e), true); } finally { setLaeuft(false); }
  };
  const mitSpeichern = () => {
    if (beteiligtenFehler(mitEntwurf, alle, s.heute)) return;
    const name = mit.name.trim();
    void ausfuehren(async () => { await legeBeteiligtenAn(beteiligtenPayload(eig.id, mitEntwurf)); setMit(LEER_MIT); }, `${name} als Miteigentümer erfasst`);
  };
  const vtrSpeichern = () => {
    if (beteiligtenFehler(vtrEntwurf, alle, s.heute)) return;
    const name = vtr.name.trim();
    const label = artLabel(vtrArt);
    void ausfuehren(async () => {
      const id = await legeBeteiligtenAn(beteiligtenPayload(eig.id, vtrEntwurf));
      if (vollmacht) {
        const dok = await ladeMieterDokumentHoch({ datei: vollmacht, mieterId: eig.id, mieterName: s.name, objektId: s.daten.zeile.objektIds[0] ?? null, kategorie: 'Vollmacht', ordner: 'eigentuemer', eigentuemer: true });
        await setzeBeteiligtenDokument(id, dok.id);
      }
      setVtr(LEER_VTR); setVollmacht(null);
    }, `${name} als ${label} hinterlegt`);
  };
  const sprecherWechseln = (b: Beteiligter) =>
    void ausfuehren(() => setzeZustellvertreter(eig.id, b.zustellvertreter ? null : b.id), b.zustellvertreter ? 'Zustellvertreter entfernt' : `${b.name} ist Zustellvertreter`);
  const dokument = (id: string | null) => (id ? s.daten.dokumente.find(d => d.id === id) ?? null : null);

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {loeschen && (
        <BestaetigenDialog titel={`${loeschen.name} entfernen?`} text={`${artLabel(loeschen.art)} wird aus der Liste entfernt. Ein hinterlegtes Vollmacht-Dokument bleibt unter „Dokumente“.`}
          bestaetigenLabel="Entfernen" laeuft={laeuft} onAbbrechen={() => setLoeschen(null)}
          onBestaetigen={() => { const b = loeschen; void ausfuehren(() => loescheBeteiligten(b.id), `${b.name} entfernt`).then(() => setLoeschen(null)); }} />
      )}
      <KpiReihe>
        <KpiTile layout="kachel" label={`Anteil ${s.name}`} wert={`${tausend(anteil)} %`} icon={ChartPieSlice} ton="acc" />
        <KpiTile layout="kachel" label="Miteigentümer" wert={mitglieder.length + ausEinheiten.length} icon={Users} ton="info" />
        <KpiTile layout="kachel" label="Zustellvertreter / Sprecher" wert={sprecher ? sprecher.name : fehlt ? 'fehlt' : s.name} icon={Megaphone} ton={fehlt ? 'warn' : 'ok'} />
      </KpiReihe>
      {fehlt && (
        <InnenKarte style={GLAS} gap={4}>
          <KartenTitel>Hinweis</KartenTitel>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
            Bei Miteigentum oder Erbengemeinschaft sollte ein gemeinsamer Zustellvertreter benannt sein (§ 25 Abs. 2 WEG: Stimmrecht nur einheitlich). Tippe auf eine Person, um sie als Sprecher festzulegen.
          </span>
        </InnenKarte>
      )}
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Eigentümergemeinschaft</KartenTitel></div>
        <PanelZeile icon={CrownSimple} ton="acc" titel={s.name} sub={`Haupteigentümer · ${tausend(anteil)} %`} />
        {mitglieder.map(b => (
          <div key={b.id} className="flex items-center min-w-0" style={{ gap: 4 }}>
            <div className="flex-1 min-w-0">
              <PanelZeile icon={b.zustellvertreter ? Megaphone : User} ton={b.zustellvertreter ? 'ok' : 'info'} titel={b.name}
                sub={[artLabel(b.art), b.anteil !== null ? `${tausend(b.anteil)} %` : '', b.email ?? ''].filter(Boolean).join(' · ')}
                chip={b.zustellvertreter ? { text: 'Zustellvertreter', ton: 'ok' } : undefined}
                onClick={laeuft ? undefined : () => sprecherWechseln(b)} ariaLabel={b.zustellvertreter ? `${b.name} als Zustellvertreter entfernen` : `${b.name} als Zustellvertreter festlegen`} />
            </div>
            <ImmoButton groesse={32} icon={X} aria-label={`${b.name} entfernen`} title="Entfernen" onClick={() => setLoeschen(b)} />
          </div>
        ))}
        {ausEinheiten.map(p => (
          <PanelZeile key={p.id} icon={User} ton="info" titel={p.name} sub={`Miteigentum · ${p.einheiten.join(', ')}`} chip={{ text: 'eigener Eigentümer', ton: 'info' }}
            onClick={() => openKontakt(p.id, 'eigentuemer')} ariaLabel={`${p.name} öffnen`} />
        ))}
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Miteigentümer hinzufügen</KartenTitel>
        <SegmentedControl<MitArt> ariaLabel="Art des Miteigentums" hoehe={34} className="self-start" wert={mitArt} onChange={setMitArt}
          segmente={MIT_ARTEN.map(a => ({ key: a.key, label: a.label, icon: MIT_ICON[a.key] }))} />
        <FeldRaster minBreite={180}>
          <TextFeld label="Name" icon={User} placeholder="Vor- und Nachname" wert={mit.name} onChange={v => setMit(x => ({ ...x, name: v }))} />
          <TextFeld label="Anteil" icon={Percent} placeholder="50" inputMode="decimal" suffix="%" wert={mit.anteil} onChange={v => setMit(x => ({ ...x, anteil: v }))} />
          <TextFeld label="E-Mail" icon={EnvelopeSimple} placeholder="optional" type="email" inputMode="email" wert={mit.email} onChange={v => setMit(x => ({ ...x, email: v }))} />
        </FeldRaster>
        {mitFehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>{mitFehler}</span>}
        <ImmoButton art="akzent" icon={Plus} className="self-start" disabled={laeuft || !mit.name.trim() || !!mitFehler} onClick={mitSpeichern}>Hinzufügen</ImmoButton>
      </InnenKarte>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>Bevollmächtigte &amp; gesetzliche Vertreter</KartenTitel></div>
        {vertreter.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Keine Vertreter hinterlegt</span>}
        {vertreter.map(b => {
          const dok = dokument(b.dokument_id);
          const gueltig = vollmachtGueltig(b, s.heute);
          return (
            <div key={b.id} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={Signature} ton={gueltig ? 'acc' : 'neutral'} titel={b.name}
                  sub={[artLabel(b.art), b.umfang ?? '', b.gueltig_bis ? `gültig bis ${deDatum(b.gueltig_bis)}` : '', dok ? 'Vollmacht hinterlegt' : ''].filter(Boolean).join(' · ')}
                  chip={gueltig ? { text: artLabel(b.art), ton: 'acc' } : { text: 'abgelaufen', ton: 'bad' }}
                  onClick={dok ? () => { void oeffneMieterDokument(dok).catch((e: unknown) => zeige(fehlerText(e), true)); } : undefined}
                  ariaLabel={dok ? `Vollmacht von ${b.name} öffnen` : undefined} />
              </div>
              <ImmoButton groesse={32} icon={X} aria-label={`${b.name} entfernen`} title="Entfernen" onClick={() => setLoeschen(b)} />
            </div>
          );
        })}
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Vertreter hinzufügen</KartenTitel>
        <Auswahl<VertreterArt> optionen={VERTRETER_ARTEN} wert={vtrArt} onChange={setVtrArt} />
        <FeldRaster minBreite={180}>
          <TextFeld label="Name" icon={User} placeholder="Vor- und Nachname" wert={vtr.name} onChange={v => setVtr(x => ({ ...x, name: v }))} />
          <TextFeld label="Gültig bis" icon={CalendarBlank} type="date" wert={vtr.gueltigBis} onChange={v => setVtr(x => ({ ...x, gueltigBis: v }))} />
          <TextFeld label="Umfang" icon={TextAlignLeft} voll placeholder="z. B. Vertretung in der ETV, Vermögenssorge" wert={vtr.umfang} onChange={v => setVtr(x => ({ ...x, umfang: v }))} />
        </FeldRaster>
        {vollmacht && <span style={{ fontSize: 12, color: 'var(--ink2)' }}>Vollmacht: {vollmacht.name} – wird beim Speichern unter „Dokumente“ abgelegt.</span>}
        {vtrFehler && <span style={{ fontSize: 12, color: 'var(--bad)' }}>{vtrFehler}</span>}
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton art="akzent" icon={Plus} disabled={laeuft || !vtr.name.trim() || !!vtrFehler} onClick={vtrSpeichern}>{laeuft ? 'Wird gespeichert …' : 'Vertreter speichern'}</ImmoButton>
          <ImmoButton icon={UploadSimple} disabled={laeuft} onClick={() => datei.current?.click()}>{vollmacht ? 'Andere Datei wählen' : 'Vollmacht hochladen'}</ImmoButton>
          <input ref={datei} type="file" hidden accept=".pdf,image/*" onChange={e => { setVollmacht(e.target.files?.[0] ?? null); e.target.value = ''; }} />
        </div>
      </InnenKarte>
    </>
  );
}

// ── Zustellung & Versandweg (Screens 53, 68) ───────────────────────────────────
const VERSAND_ICON: Record<Versandweg, Icon> = { post: Envelope, email: At, portal: DeviceMobile };
const t = (v: unknown) => (typeof v === 'string' ? v : '');

export function ZustellungPanel() {
  const s = useEigSeite();
  const e = s.daten.eigentuemer;
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const gespeichert: ZustellAdresse = { co: t(e.zustell_co), strasse: t(e.zustell_strasse), hausnummer: t(e.zustell_hausnummer), plz: t(e.zustell_plz), stadt: t(e.zustell_stadt) };
  const [adr, setAdr] = useState<ZustellAdresse>(gespeichert);
  const weg = versandweg(e.versandweg, s.portal === 'aktiv', s.email);
  const einwilligung = t(e.textform_einwilligung_am) || null;
  const post = postPflicht(e.post_pflicht);
  const abweichend = hatAbweichendeAdresse(gespeichert);
  const wohnanschrift = adressText({ strasse: t(e.strasse), hausnummer: t(e.hausnummer), plz: t(e.plz), stadt: t(e.stadt) });
  const sprecher = s.daten.beteiligte.find(b => b.zustellvertreter) ?? null;
  const geaendert = (Object.keys(adr) as (keyof ZustellAdresse)[]).some(k => adr[k].trim() !== gespeichert[k]);

  const speichere = async (f: ZustellFelder, text: string) => {
    setLaeuft(true);
    try { await speichereZustellung(e.id, f); zeige(text); meldeEigentuemerGeaendert(e.id); } catch (x) { zeige(fehlerText(x), true); } finally { setLaeuft(false); }
  };
  const leer = (v: string) => v.trim() || null;
  const adresseSpeichern = () => void speichere({
    zustell_co: leer(adr.co), zustell_strasse: leer(adr.strasse), zustell_hausnummer: leer(adr.hausnummer), zustell_plz: leer(adr.plz), zustell_stadt: leer(adr.stadt),
  }, 'Zustelladresse gespeichert');
  const adresseEntfernen = () => {
    setAdr({ co: '', strasse: '', hausnummer: '', plz: '', stadt: '' });
    void speichere({ zustell_co: null, zustell_strasse: null, zustell_hausnummer: null, zustell_plz: null, zustell_stadt: null }, 'Zustellung an Wohnanschrift');
  };
  const setzeAdr = (k: keyof ZustellAdresse) => (v: string) => setAdr(x => ({ ...x, [k]: v }));
  const wegLabel = VERSANDWEGE.find(v => v.key === weg.weg)?.label ?? '';

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Bevorzugter Versandweg</KartenTitel>
        <SegmentedControl<Versandweg> ariaLabel="Bevorzugter Versandweg" hoehe={34} className="self-start" wert={weg.weg}
          onChange={v => { if (!laeuft) void speichere({ versandweg: v }, `Versandweg: ${VERSANDWEGE.find(x => x.key === v)?.label ?? v}`); }}
          segmente={VERSANDWEGE.map(v => ({ key: v.key, label: v.label, icon: VERSAND_ICON[v.key] }))} />
        {!weg.gespeichert && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Vorschlag aus Portalzugang und E-Mail – noch nicht festgelegt.</span>}
        <div className="flex flex-col" style={{ gap: 6 }}>
          <SchalterZeile an={!!einwilligung} disabled={laeuft} icon={SealCheck} label="Einwilligung Textform (§ 126b BGB) – Einladungen & Abrechnungen elektronisch"
            zusatz={einwilligung ? `erteilt am ${deDatum(einwilligung)}` : undefined}
            onChange={an => void speichere({ textform_einwilligung_am: an ? s.heute : null }, an ? `Einwilligung zur Textform erfasst (${deDatum(s.heute)})` : 'Einwilligung widerrufen – Versand per Post')} />
          {POST_ARTEN.map(p => (
            <SchalterZeile key={p.key} an={post[p.key]} disabled={laeuft} icon={Mailbox} label={`${p.label} · ${post[p.key] ? 'immer per Post' : 'Versandweg wie oben'}`}
              onChange={an => void speichere({ post_pflicht: { ...post, [p.key]: an } }, `${p.label}: ${an ? 'immer per Post' : 'Versandweg wie oben'}`)} />
          ))}
        </div>
      </InnenKarte>
      {textformWarnung(weg.weg, einwilligung) && (
        <InnenKarte style={{ background: 'var(--bad-soft)' }} gap={4}>
          <KartenTitel>Achtung</KartenTitel>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
            Ohne Einwilligung zur Textform müssen fristgebundene Schreiben (z. B. ETV-Einladung, Abrechnung) zusätzlich per Post zugestellt werden.
          </span>
        </InnenKarte>
      )}
      <InnenKarte style={GLAS}>
        <KartenTitel>Abweichende Zustelladresse</KartenTitel>
        <FeldRaster minBreite={150}>
          <TextFeld label="c/o / Empfänger" icon={User} voll placeholder="z. B. Steuerbüro Hahn" wert={adr.co} onChange={setzeAdr('co')} />
          <TextFeld label="Straße" icon={Signpost} wert={adr.strasse} onChange={setzeAdr('strasse')} />
          <TextFeld label="Nr." icon={Hash} wert={adr.hausnummer} onChange={setzeAdr('hausnummer')} />
          <TextFeld label="PLZ" icon={MapPin} inputMode="numeric" wert={adr.plz} onChange={setzeAdr('plz')} />
          <TextFeld label="Stadt" icon={MapTrifold} wert={adr.stadt} onChange={setzeAdr('stadt')} />
        </FeldRaster>
        {geaendert && !hatAbweichendeAdresse(adr) && <span style={{ fontSize: 12, color: 'var(--bad)' }}>Straße, PLZ oder Stadt angeben</span>}
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton art="akzent" icon={Check} disabled={laeuft || !geaendert || !hatAbweichendeAdresse(adr)} onClick={adresseSpeichern}>Zustelladresse speichern</ImmoButton>
          {abweichend && <ImmoButton icon={X} disabled={laeuft} onClick={adresseEntfernen}>Entfernen – Wohnanschrift nutzen</ImmoButton>}
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS} gap={0}>
        <KartenTitel>Aktuelle Zustellung</KartenTitel>
        <DatenZeile label="Versandweg" wert={wegLabel} />
        <DatenZeile label="Adresse" wert={abweichend ? adressText(gespeichert) : wohnanschrift || <span style={{ color: 'var(--bad)' }}>keine Anschrift hinterlegt</span>} />
        <DatenZeile label="Textform" wert={<span style={{ color: einwilligung ? 'var(--ok)' : 'var(--bad)' }}>{einwilligung ? `eingewilligt am ${deDatum(einwilligung)}` : 'nicht erteilt'}</span>} />
        {sprecher && <DatenZeile label="Zustellvertreter" wert={sprecher.name} />}
      </InnenKarte>
    </>
  );
}

// ── Termine (Screen 54) ────────────────────────────────────────────────────────
export function TerminePanel() {
  const s = useEigSeite();
  const { onSearchNavigate } = useIslandActions();
  const navigate = useNavigate();
  const [neu, setNeu] = useState(false);
  const [toast, zeige] = useToastPill();
  const eig = s.daten.eigentuemer;
  const objektName = new Map(s.daten.objekte.map(o => [o.id, o.bezeichnung ?? '']));
  // Termine am Eigentümer und ETV der WEGs (am Objekt), ohne Doppelte, nach Datum
  const termine = useMemo(() => {
    const alle = [...s.daten.termine, ...s.daten.etvTermine];
    return [...new Map(alle.map(t => [t.id, t])).values()].sort((a, b) => a.start_datum.localeCompare(b.start_datum));
  }, [s.daten.termine, s.daten.etvTermine]);
  const oeffne = (id: string) => { if (onSearchNavigate) onSearchNavigate('/admin/kalender', id, 'termin'); else navigate('/admin/kalender'); };
  const ersteObjekt = s.daten.zeile.objektIds[0] ?? null;
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {neu && (
        <TerminFormularKarte termin={null} prefillObjektId={ersteObjekt} prefillEigentuemerId={eig.id} prefillTyp={s.daten.zeile.werte.hasWEG ? 'meeting' : null}
          onAbbrechen={() => setNeu(false)} onGespeichert={() => { setNeu(false); zeige('Termin angelegt'); meldeEigentuemerGeaendert(eig.id); }} />
      )}
      <InnenKarte style={GLAS}>
        <KartenTitel rechts={!neu ? <ImmoButton groesse={36} icon={Plus} onClick={() => setNeu(true)}>Termin anlegen</ImmoButton> : undefined}>
          {`${termine.length} ${termine.length === 1 ? 'Termin' : 'Termine'}`}
        </KartenTitel>
        {termine.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine anstehenden Termine.</span>}
        <div className="flex flex-col">
          {termine.map(t => {
            const d = new Date(t.start_datum);
            const zeit = Number.isNaN(d.getTime()) || t.start_datum.length <= 10 ? 'ganztägig' : `${ZEIT.format(d)} Uhr`;
            const ort = t.objekt_id ? objektName.get(t.objekt_id) ?? '' : '';
            const heute = t.start_datum.slice(0, 10) === s.heute;
            return (
              <PanelZeile key={t.id} icon={CalendarCheck} ton="acc" titel={t.titel || 'Termin'} sub={[t.typ ?? '', zeit, ort].filter(Boolean).join(' · ')}
                rechts={deDatum(t.start_datum.slice(0, 10))} chip={heute ? { text: 'heute', ton: 'ok' } : undefined}
                onClick={() => oeffne(t.id)} ariaLabel={`${t.titel || 'Termin'} im Kalender öffnen`} />
            );
          })}
        </div>
      </InnenKarte>
      <div className="flex">
        <ImmoButton groesse={44} icon={CalendarCheck} onClick={() => navigate('/admin/kalender')}>Kalender öffnen</ImmoButton>
      </div>
    </>
  );
}

// ── Kommunikation (Screen 55) ──────────────────────────────────────────────────
type LogTyp = 'anruf' | 'mail' | 'meeting' | 'notiz';
const LOG_TYPEN: readonly { key: LogTyp; label: string; icon: Icon }[] = [
  { key: 'anruf', label: 'Anruf', icon: Phone }, { key: 'mail', label: 'Mail', icon: EnvelopeSimple },
  { key: 'meeting', label: 'Meeting', icon: UsersThree }, { key: 'notiz', label: 'Notiz', icon: Note },
];

export function KommunikationPanel() {
  const s = useEigSeite();
  const eig = s.daten.eigentuemer;
  const [typ, setTyp] = useState<LogTyp>('anruf');
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  // „Schnell loggen“ wie bei den Mietern: Anruf/Mail/Meeting als Aktivität, Notiz als Kontakt-Notiz
  const eintragen = async () => {
    const b = text.trim();
    if (!b) return;
    setLaeuft(true);
    try {
      if (typ === 'notiz') await speichereNotiz('eigentuemer', eig.id, b);
      else await logAktivitaet('eigentuemer', eig.id, { typ, betreff: b, richtung: typ === 'meeting' ? null : 'ausgehend' });
      setText('');
      zeige(`${LOG_TYPEN.find(t => t.key === typ)?.label ?? 'Eintrag'} protokolliert`);
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht eingetragen: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Schnell loggen</KartenTitel>
        <SegmentedControl ariaLabel="Art" hoehe={34} className="self-start" wert={typ} onChange={setTyp} segmente={LOG_TYPEN.map(t => ({ key: t.key, label: t.label, icon: t.icon }))} />
        <form className="flex flex-col" style={{ gap: 12 }} onSubmit={ev => { ev.preventDefault(); void eintragen(); }}>
          <TextFeld label="Worum ging es?" icon={ChatCircleText} wert={text} onChange={setText} placeholder="Kurzbeschreibung" maxLength={300} />
          <div className="flex justify-end">
            <ImmoButton type="submit" art="akzent" groesse={44} icon={Plus} disabled={laeuft || !text.trim()}>{laeuft ? 'Wird eingetragen …' : 'Eintragen'}</ImmoButton>
          </div>
        </form>
      </InnenKarte>
      <KartenTitel>Verlauf</KartenTitel>
      <AktivitaetBereich aktivitaeten={s.daten.aktivitaeten} hinweis={null} />
    </>
  );
}

// ── Dokumente (Screen 56) ──────────────────────────────────────────────────────
export function DokumentePanel() {
  const s = useEigSeite();
  const eig = s.daten.eigentuemer;
  const [suche, setSuche] = useState('');
  const [kategorie, setKategorie] = useState<string>('alle');
  const [laedtHoch, setLaedtHoch] = useState(false);
  const datei = useRef<HTMLInputElement>(null);
  const [toast, zeige] = useToastPill();
  const docs = s.daten.dokumente;
  const kategorien = useMemo(() => [...new Set(docs.map(d => (d.kategorie ?? '').trim() || 'Ohne Kategorie'))].sort((a, b) => a.localeCompare(b, 'de')), [docs]);
  const sichtbar = useMemo(() => {
    const q = normalisiereSuche(suche);
    return docs.filter(d => (kategorie === 'alle' || ((d.kategorie ?? '').trim() || 'Ohne Kategorie') === kategorie)
      && (!q || normalisiereSuche(`${d.titel ?? ''} ${d.datei_name ?? ''} ${d.kategorie ?? ''}`).includes(q)));
  }, [docs, suche, kategorie]);

  const hochladen = async (f: File) => {
    setLaedtHoch(true);
    try {
      await ladeMieterDokumentHoch({ datei: f, mieterId: eig.id, mieterName: s.name, objektId: s.daten.zeile.objektIds[0] ?? null, kategorie: 'Eigentümer', ordner: 'eigentuemer', eigentuemer: true });
      zeige(`${f.name} hochgeladen`);
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Upload fehlgeschlagen: ${fehlerText(err)}`, true);
    } finally {
      setLaedtHoch(false);
      if (datei.current) datei.current.value = '';
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Suchen</KartenTitel>
        <TextFeld label="Dokument" icon={MagnifyingGlass} wert={suche} onChange={setSuche} placeholder="Dokumente durchsuchen" />
        {kategorien.length > 1 && (
          <SegmentedControl ariaLabel="Kategorie" hoehe={30} className="self-start" wert={kategorie} onChange={setKategorie}
            segmente={[{ key: 'alle', label: 'Alle' }, ...kategorien.map(k => ({ key: k, label: k }))]} />
        )}
        <div className="flex justify-end">
          <input ref={datei} type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) void hochladen(f); }} />
          <ImmoButton art="akzent" groesse={44} icon={UploadSimple} disabled={laedtHoch} onClick={() => datei.current?.click()}>{laedtHoch ? 'Wird hochgeladen …' : 'Hochladen'}</ImmoButton>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS} padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>{`${sichtbar.length} ${sichtbar.length === 1 ? 'Dokument' : 'Dokumente'}`}</KartenTitel></div>
        {sichtbar.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>{docs.length ? 'Keine Dokumente gefunden' : 'Noch keine Dokumente zu diesem Eigentümer.'}</span>}
        {sichtbar.map(d => (
          <PanelZeile key={d.id} icon={FolderSimple} ton="acc" titel={d.titel || d.datei_name || 'Dokument'}
            sub={[(d.kategorie ?? '').trim() || 'Ohne Kategorie', deDatum(d.created_at.slice(0, 10))].join(' · ')} rechts={<ArrowSquareOut size={15} color="var(--ink3)" />}
            onClick={() => { void oeffneMieterDokument(d).then(ok => { if (!ok) zeige('Datei nur im Dokumentenbereich erreichbar', true); }); }} ariaLabel={`${d.titel || d.datei_name || 'Dokument'} öffnen`} />
        ))}
      </InnenKarte>
    </>
  );
}

// ── Notizen (Screen 57) ────────────────────────────────────────────────────────
export function NotizenPanel() {
  const s = useEigSeite();
  const eig = s.daten.eigentuemer;
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [toast, zeige] = useToastPill();
  const speichere = async () => {
    const t = text.trim();
    if (!t) return;
    setLaeuft(true);
    try {
      await speichereNotiz('eigentuemer', eig.id, t);
      setText('');
      zeige('Notiz gespeichert');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht gespeichert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  const loesche = async (id: string) => {
    try {
      await loescheNotiz(id);
      zeige('Notiz gelöscht');
      meldeEigentuemerGeaendert(eig.id);
    } catch (err) {
      zeige(`Nicht gelöscht: ${fehlerText(err)}`, true);
    }
  };
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Neue Notiz</KartenTitel>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder={`Notiz zu ${s.name} …`} rows={4} aria-label="Neue Notiz"
          style={{ width: '100%', borderRadius: 15, padding: 14, border: 'none', resize: 'vertical', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, color: 'var(--ink)', outline: 'none', fontFamily: 'inherit' }} />
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Plus} disabled={laeuft || !text.trim()} onClick={() => { void speichere(); }}>{laeuft ? 'Wird gespeichert …' : 'Notiz speichern'}</ImmoButton>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel rechts={s.daten.notizen.length ? `${s.daten.notizen.length}` : undefined}>Notizen</KartenTitel>
        {s.daten.notizen.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Notizen</span>}
        <div className="flex flex-col">
          {s.daten.notizen.map(n => (
            <div key={n.id} className="flex items-start" style={{ gap: 12, padding: '10px 6px', borderBottom: '1px solid var(--line)' }}>
              <span className="flex flex-col flex-1 min-w-0" style={{ gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.text}</span>
                <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{deDatum(n.created_at.slice(0, 10))}</span>
              </span>
              <ImmoButton groesse={32} pille={false} art="bad" icon={Trash} aria-label="Notiz löschen" title="Notiz löschen" onClick={() => { void loesche(n.id); }} />
            </div>
          ))}
        </div>
      </InnenKarte>
    </>
  );
}

// ── KI-Zusammenfassung (Screen 59) ─────────────────────────────────────────────
export function KiPanel() {
  const s = useEigSeite();
  const z = s.daten.zeile;
  const w = z.werte;
  const fr = freigabeKennzahlen(s.daten.freigaben);
  const fakten: { label: string; wert: string; farbe?: string }[] = [
    { label: 'Rollen', wert: w.rollenL || '–' },
    { label: 'Status', wert: statusText(z), farbe: `var(--${EIG_STATUS[z.status].ton === 'neutral' ? 'ink' : EIG_STATUS[z.status].ton})` },
    { label: 'Saldo', wert: 'startet mit der Buchhaltung', farbe: 'var(--ink3)' },
    { label: 'Offene Freigaben', wert: String(fr.offen), farbe: fr.offen ? 'var(--warn)' : undefined },
    ...(w.verwaltet ? [{ label: 'Belegung', wert: s.portfolio.belegung === null ? '–' : `${s.portfolio.belegung} %` }, { label: 'Auszahlung / Monat', wert: eur(w.ausz, { nachkomma: 0 }), farbe: w.ausz < 0 ? 'var(--bad)' : undefined }] : []),
    ...(w.hasWEG ? [{ label: 'MEA', wert: `${tausend(s.portfolio.mea)} / 1.000` }, { label: 'Hausgeld / Monat', wert: eur(w.hgM, { nachkomma: 0 }) }] : []),
    { label: 'Miteigentümer', wert: String(new Set(s.daten.miteigentuemer.map(m => m.id)).size + s.daten.beteiligte.filter(b => b.typ === 'miteigentuemer').length) },
    ...(zustellvertreterFehlt(s.daten.beteiligte) ? [{ label: 'Zustellvertreter', wert: 'fehlt', farbe: 'var(--warn)' }] : []),
    { label: 'Versandweg', wert: VERSANDWEGE.find(v => v.key === versandweg(s.daten.eigentuemer.versandweg, s.portal === 'aktiv', s.email).weg)?.label ?? '–' },
    { label: 'Portal', wert: PORTAL_ANZEIGE[s.portal].label, farbe: PORTAL_ANZEIGE[s.portal].farbe },
  ];
  return (
    <>
      <InnenKarte style={GLAS}>
        <div className="flex items-start" style={{ gap: 14 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: 'var(--acc-soft)' }}><Sparkle weight="fill" size={20} color="var(--acc)" /></span>
          <div className="flex flex-col min-w-0" style={{ gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Zusammenfassung</span>
            <span style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)' }}>{s.zusammenfassung}</span>
            <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Automatisch aus den Fakten erstellt – eine KI-Formulierung wird noch angebunden.</span>
          </div>
        </div>
      </InnenKarte>
      <InnenKarte style={GLAS}>
        <KartenTitel>Fakten</KartenTitel>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', columnGap: 28 }}>
          {fakten.map(f => (
            <div key={f.label} className="flex items-center justify-between" style={{ gap: 12, padding: '11px 0', borderBottom: '1px solid var(--line)' }}>
              <span style={{ fontSize: 14, color: 'var(--ink2)' }}>{f.label}</span>
              <span className="immo-zahl text-right" style={{ fontSize: 14, fontWeight: 800, color: f.farbe ?? 'var(--ink)' }}>{f.wert}</span>
            </div>
          ))}
        </div>
      </InnenKarte>
    </>
  );
}
